import { getDriver, getSession } from '../config/neo4j';
import logger from '../utils/logger';

const handleError = (error, operation) => {
  logger.error(`Error during ${operation}:`, error);
  if (error.message.includes('Pool is closed')) {
    throw new Error('Database connection lost. Please refresh the page and try again.');
  }
  throw error;
};

export const createUserProfile = async (userId, profileData) => {
  if (!userId) throw new Error('User ID is required');
  logger.info('Creating user profile for:', userId);
  
  const session = getSession();
  try {
    const result = await session.executeWrite(async (tx) => {
      const { socialLinks, ...userData } = profileData;
      logger.info('Creating user node with data:', userData);
      
      await tx.run(
        `
        MERGE (root:SmartEcoCard {id: 'root'})
        MERGE (u:User {userId: $userId})
        SET u += $userData
        MERGE (root)-[:HAS_USER]->(u)
        RETURN u
        `,
        { userId, userData }
      );

      if (socialLinks && socialLinks.length > 0) {
        logger.info('Creating social links:', socialLinks);
        await tx.run(
          `
          MATCH (u:User {userId: $userId})-[r:HAS_SOCIAL_LINK]->(s:SocialLink)
          DELETE r, s
          `,
          { userId }
        );

        for (const link of socialLinks) {
          await tx.run(
            `
            MATCH (u:User {userId: $userId})
            CREATE (s:SocialLink {platform: $platform, url: $url})
            CREATE (u)-[:HAS_SOCIAL_LINK]->(s)
            `,
            {
              userId,
              platform: link.platform,
              url: link.url
            }
          );
        }
      }
    });
    logger.info('Successfully created user profile');
    return result;
  } catch (error) {
    handleError(error, 'createUserProfile');
  } finally {
    await session.close();
  }
};

export const getUserProfile = async (userId) => {
  const session = await getSession();
  try {
    logger.info('Fetching user profile for:', userId);
    
    // First ensure the user is under SmartEcoCard
    await session.executeWrite(async (tx) => {
      await tx.run(`
        MATCH (u:User {userId: $userId})
        MERGE (root:SmartEcoCard {id: 'root'})
        MERGE (root)-[:HAS_USER]->(u)
      `, { userId });
    });

    // Then fetch the profile
    const result = await session.run(
      `
      MATCH (root:SmartEcoCard {id: 'root'})-[:HAS_USER]->(u:User {userId: $userId})
      RETURN u {
        .name,
        .email,
        .phone,
        .role,
        .company,
        .companyWebsite,
        .photoURL,
        .socialLinks,
        .phonePrivacy,
        .emailPrivacy,
        .userId
      } as user
      `,
      { userId }
    );

    if (result.records.length === 0) {
      logger.info('No profile found for user:', userId);
      return null;
    }

    const userProfile = result.records[0].get('user');
    logger.info('Retrieved user profile:', userProfile);

    // Handle nested objects that might be stringified
    const processedProfile = {
      ...userProfile,
      // Ensure these fields are strings if they exist
      name: userProfile.name?.toString() || '',
      email: userProfile.email?.toString() || '',
      phone: userProfile.phone?.toString() || '',
      role: userProfile.role?.toString() || '',
      company: userProfile.company?.toString() || '',
      companyWebsite: userProfile.companyWebsite?.toString() || '',
      // Handle social links
      socialLinks: userProfile.socialLinks ? JSON.parse(userProfile.socialLinks) : [],
      // Set default privacy settings
      phonePrivacy: userProfile.phonePrivacy || 'public',
      emailPrivacy: userProfile.emailPrivacy || 'public'
    };

    logger.info('Processed profile:', processedProfile);
    return processedProfile;
  } catch (error) {
    logger.error('Error getting user profile:', error);
    throw error;
  } finally {
    await session.close();
  }
};

export const updateUserProfile = async (userId, profileData) => {
  const session = await getSession();
  try {
    const processedData = {
      ...profileData,
      socialLinks: profileData.socialLinks ? JSON.stringify(profileData.socialLinks) : '[]',
      updatedAt: new Date().toISOString()
    };

    logger.debug('Updating user profile with data:', { userId, ...processedData });

    const result = await session.run(
      `
      MATCH (root:SmartEcoCard {id: 'root'})
      MERGE (u:User {userId: $userId})
      SET u += $profileData
      MERGE (root)-[:HAS_USER]->(u)
      RETURN u
      `,
      {
        userId,
        profileData: processedData
      }
    );
    return result.records[0].get('u').properties;
  } catch (error) {
    logger.error('Error updating user profile:', error);
    throw error;
  } finally {
    await session.close();
  }
};

export const deleteUserProfile = async (userId) => {
  if (!userId) throw new Error('User ID is required');
  logger.info('Deleting user profile for:', userId);
  
  const session = getSession();
  try {
    await session.executeWrite(async (tx) => {
      await tx.run(
        `
        MATCH (root:SmartEcoCard {id: 'root'})-[r:HAS_USER]->(u:User {userId: $userId})
        DETACH DELETE u
        `,
        { userId }
      );
    });
    logger.info('Successfully deleted user profile');
  } catch (error) {
    handleError(error, 'deleteUserProfile');
  } finally {
    await session.close();
  }
};

export const migrateToSmartEcoCard = async () => {
  const session = getSession();
  try {
    logger.info('Starting migration of users to SmartEcoCard node');
    await session.executeWrite(async (tx) => {
      // Delete any duplicate root nodes, keeping the oldest one
      await tx.run(`
        MATCH (root:SmartEcoCard)
        WITH root
        ORDER BY root.createdAt ASC
        WITH collect(root) as roots
        WITH roots[0] as keepRoot, roots[1..] as removeRoots
        UNWIND removeRoots as removeRoot
        MATCH (removeRoot)-[r:HAS_USER]->(u:User)
        WITH removeRoot, u, keepRoot
        MERGE (keepRoot)-[:HAS_USER]->(u)
        DETACH DELETE removeRoot
      `);

      // Create root node if it doesn't exist
      await tx.run(`
        MERGE (root:SmartEcoCard {id: 'root'})
        ON CREATE SET root.createdAt = timestamp()
        RETURN root
      `);

      // Find all users not connected to SmartEcoCard and connect them
      const result = await tx.run(`
        MATCH (u:User)
        WHERE NOT (u)<-[:HAS_USER]-(:SmartEcoCard)
        WITH u
        MATCH (root:SmartEcoCard {id: 'root'})
        MERGE (root)-[:HAS_USER]->(u)
        RETURN count(u) as migratedCount
      `);

      const migratedCount = result.records[0].get('migratedCount').toNumber();
      logger.info(`Migration complete. Connected ${migratedCount} users to SmartEcoCard node`);
      return migratedCount;
    });
  } catch (error) {
    logger.error('Error during migration:', error);
    throw error;
  } finally {
    await session.close();
  }
};

export const testNeo4jConnection = async () => {
  const session = await getSession();
  try {
    const result = await session.run('RETURN $userId as userId', { userId: 'test' });
    return {
      success: true,
      userId: result.records[0]?.get('userId')
    };
  } catch (error) {
    logger.error('Neo4j connection test failed:', error);
    throw error;
  } finally {
    await session.close();
  }
};
